// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';

function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const styles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
  },
  main: {
    marginLeft: 'var(--sidebar-width)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: 'var(--ka-cream)',
    minWidth: 0, // let content shrink instead of overflowing on mobile
  },
  topBar: {
    height: 56,
    background: 'var(--ka-white)',
    borderBottom: '1px solid var(--ka-gray-200)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 32px',
    boxShadow: 'var(--shadow-sm)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  hamburger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    marginRight: 12,
    marginLeft: -8,
    border: 'none',
    background: 'none',
    color: 'var(--ka-navy)',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    borderRadius: 8,
  },
  topBarTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--ka-navy)',
    letterSpacing: '0.01em',
  },
  topBarSub: {
    marginLeft: 'auto',
    fontSize: 12,
    color: 'var(--ka-gray-400)',
  },
  content: {
    flex: 1,
    padding: '32px',
    maxWidth: 1200,
    width: '100%',
    boxSizing: 'border-box',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 150,
  },
};

export default function Layout() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Close the drawer whenever we cross back to desktop.
  useEffect(() => {
    if (!isMobile) setOpen(false);
  }, [isMobile]);

  const main = {
    ...styles.main,
    ...(isMobile ? { marginLeft: 0 } : {}),
  };
  const topBar = {
    ...styles.topBar,
    ...(isMobile ? { padding: '0 16px' } : {}),
  };
  const content = {
    ...styles.content,
    ...(isMobile ? { padding: '20px 16px' } : {}),
  };

  return (
    <div style={styles.wrapper}>
      <Sidebar isMobile={isMobile} open={open} onClose={() => setOpen(false)} />

      {isMobile && open && (
        <div style={styles.backdrop} onClick={() => setOpen(false)} />
      )}

      <main style={main}>
        <div style={topBar}>
          {isMobile && (
            <button
              style={styles.hamburger}
              aria-label="Open menu"
              onClick={() => setOpen((v) => !v)}
            >
              ☰
            </button>
          )}
          <span style={styles.topBarTitle}>
            {isMobile ? 'Faculty Tool Kit' : 'King’s Academy — Faculty Tool Kit'}
          </span>
          {!isMobile && (
            <span style={styles.topBarSub}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          )}
        </div>
        <div style={content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}